import {observer} from 'mobx-react-lite'
import React, {useEffect, useState} from 'react'
import {Lang} from 'yougile-lang'
import {Tracking, TrackingEvent} from '@common/tracking'
import {Icon} from '@components/common/base/icon'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {rightPanelStore} from '@components/right-panel/right-panel'
import {useByTimeStore} from '@components/statistics/by-time/by-time-store'
import {
  TwCompletedSign,
  TwSelected,
  TwTaskNameTitle
} from '@components/statistics/by-time/containers'
import {DataItem} from '@components/statistics/by-time/hooks'
import {useSyncEvent} from '@containers/hooks'
import {ChatUtils} from '@extensions/chat-utils'
import {UserIcon} from '@extensions/sticker/views-web/user/user-sticker-state'
import {
  TaskNameColumnContext,
  TaskNameColumnStore,
  useTaskNameColumnStore
} from './task-name-column-store'

export const TaskNameColumn = ({data}: {data: DataItem}) => {
  const [taskNameStore] = useState(() => new TaskNameColumnStore(data.taskId))

  useEffect(() => {
    taskNameStore.syncTaskId(data.taskId)
    taskNameStore.syncTaskData()
  }, [data.taskId])

  return (
    <TaskNameColumnContext.Provider value={taskNameStore}>
      <TaskNameColumnBase />
    </TaskNameColumnContext.Provider>
  )
}

export const TaskNameColumnBase = observer(() => {
  const [state, store] = useTaskNameColumnStore()
  const [byTimeState] = useByTimeStore()

  const panelIsOpened = rightPanelStore.state.chatPanel.opened
  const taskIsOpened =
    panelIsOpened && byTimeState.openedTaskId === state.taskId

  useSyncEvent(
    'current',
    {filter: state.taskId, debounceTime: 100},
    () => {
      store.syncTaskData()
    },
    [state.taskId]
  )

  const selectTaskHandler = () => {
    void Tracking.send(TrackingEvent.taskOpenTaskRightPanelViaBoard)
    ChatUtils.toggleChat(store.getCurrentTask())
  }

  return (
    <div
      className="flex items-center w-full cursor-pointer group"
      onClick={() => selectTaskHandler()}
    >
      {taskIsOpened && <TwSelected />}
      <div className="p-4">
        {!state.isDone && <Icon icon="IconCustomCircle" />}
        {state.isDone && <TwCompletedSign $completed={state.isDone} />}
      </div>
      {state.isArchived && (
        <div className="p-4">
          <Icon icon="IconCustomArchive" color="placeholder" />
        </div>
      )}
      <Tooltip offset={[0, 10]} content={state.title}>
        <TwTaskNameTitle
          $taskIsOpened={taskIsOpened}
          $taskIsDone={state.isDone}
        >
          {state.title}
        </TwTaskNameTitle>
      </Tooltip>
      {!!state.assignedUsers?.length && (
        <div className="ml-auto min-w-32 flex pr-4">
          <div className="ml-auto">
            {state.assignedUsers.slice(0, 3).map((userId, ix) => (
              <UserIcon
                key={userId}
                userId={userId}
                hideTitle={true}
                shift={17}
                zIndex={5 - ix}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
})

export const NameColumn = observer(() => {
  return <div className="w-full pl-[28px] mt-auto">{Lang.msg.tsTaskName}</div>
})
